// Story 1.18 — List of refundable top-ups. Per-item "Refund this top-up"
// button opens the RefundModal with the selected row.

import { Button } from "@greenhold/components";

import { EmptyState } from "../../components/EmptyState";
import type { RefundableTopupItem } from "./useRefundEligibleList";

function formatEur(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const euros = Math.floor(abs / 100);
  const remainder = abs % 100;
  return `${sign}€${euros}.${remainder.toString().padStart(2, "0")}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

type RefundEligibleListProps = {
  items: RefundableTopupItem[];
  onSelect: (item: RefundableTopupItem) => void;
  /** Money held by the caller's live offers — see <HeldFundsNotice>. An
   *  offer hold zeroes every row, so an empty list with money held must not
   *  be reported as "nothing to refund". */
  heldCents?: number;
};

export function RefundEligibleList({
  items,
  onSelect,
  heldCents = 0,
}: RefundEligibleListProps) {
  if (items.length === 0) {
    return (
      <EmptyState
        aria-label="No refundable top-ups"
        description={
          heldCents > 0
            ? `Nothing is refundable right now because ${formatEur(heldCents)} of your balance is held in live offers. Withdraw an offer to release it — the 14-day window keeps running until you do.`
            : "No refundable top-ups in the last 14 days. PSD2 Article 71 lets you refund unspent top-ups for up to 14 days from the top-up date."
        }
      />
    );
  }

  return (
    <ul className="flex flex-col gap-3">
      {items.map((item) => {
        const urgent = item.days_remaining <= 3;
        return (
          <li
            key={item.ledger_entry_id}
            className="flex flex-col gap-3 rounded-lg border bg-card p-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex flex-col gap-1">
              <div className="font-body text-sm">
                Topped up{" "}
                <span className="font-semibold tabular-nums">
                  {formatEur(item.original_amount_cents)}
                </span>{" "}
                on {formatDate(item.topped_up_at)}
              </div>
              <div className="font-body text-xs text-muted-foreground">
                Refundable:{" "}
                <span className="font-semibold tabular-nums text-foreground">
                  {formatEur(item.refundable_amount_cents)}
                </span>{" "}
                · <span className="font-mono">{item.card_hint}</span>
              </div>
              <div
                className={`font-body text-xs ${
                  urgent ? "text-amber-700" : "text-muted-foreground"
                }`}
              >
                {item.days_remaining} day{item.days_remaining === 1 ? "" : "s"}{" "}
                remaining in PSD2 window
              </div>
            </div>
            <Button
              type="button"
              variant="primary"
              onClick={() => onSelect(item)}
            >
              Refund this top-up
            </Button>
          </li>
        );
      })}
    </ul>
  );
}
